// src/components/ServerModal.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Drawer, { DrawerSection, Field, inputClass } from './Drawer';
import ErrorAlert from './ErrorAlert';
import PrimaryButton from './PrimaryButton';
import SecondaryButton from './SecondaryButton';
import { IconServer } from '../icons';

const ServerModal = ({ server, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    healthRoute: '',
    ipAddress: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (server) {
      setFormData({
        name: server.name || '',
        description: server.description || '',
        healthRoute: server.healthRoute || '',
        ipAddress: server.ipAddress || ''
      });
    }
  }, [server]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim() || !formData.healthRoute.trim()) {
      setError('Name and Health Route are required');
      return;
    }

    try {
      setLoading(true);

      if (server) {
        await axios.put(`/api/servers/${server._id}`, formData);
      } else {
        await axios.post('/api/servers', formData);
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving server:', err);
      setError(err.response?.data?.message || 'Failed to save server');
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <>
      <SecondaryButton onClick={onClose} disabled={loading} className="w-[160px] h-10">
        Cancel
      </SecondaryButton>
      <PrimaryButton
        type="submit"
        form="server-form"
        loading={loading}
        disabled={!formData.name.trim() || !formData.healthRoute.trim()}
        className="w-[160px] h-10"
      >
        {server ? 'Save' : 'Add server'}
      </PrimaryButton>
    </>
  );

  return (
    <Drawer
      isOpen
      onClose={onClose}
      closeDisabled={loading}
      title={server ? 'Edit Server' : 'Add Server'}
      icon={<IconServer size={18} />}
      footer={footer}
    >
      <form id="server-form" onSubmit={handleSubmit}>
        <div className="flex flex-col py-5 gap-5">
          <DrawerSection title="Server Info">
            <ErrorAlert message={error} onDismiss={() => setError(null)} />

            <Field label="Server Name *">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
                className={inputClass}
                placeholder="My Application Server"
                required
              />
            </Field>

            <Field label="Description">
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                disabled={loading}
                rows="3"
                className={`${inputClass} resize-none`}
                placeholder="Optional description of the server"
              />
            </Field>

            <Field
              label="Health Check URL *"
              hint="Full URL to check server health (e.g. https://example.com/api/health)"
            >
              <input
                type="url"
                id="healthRoute"
                name="healthRoute"
                value={formData.healthRoute}
                onChange={handleChange}
                disabled={loading}
                className={inputClass}
                placeholder="https://example.com/health"
                required
              />
            </Field>

            <Field label="IP Address" hint="Optional server IP address (IPv4 or IPv6)">
              <input
                type="text"
                id="ipAddress"
                name="ipAddress"
                value={formData.ipAddress}
                onChange={handleChange}
                disabled={loading}
                className={inputClass}
                placeholder="192.168.1.1 or 2001:db8::1"
              />
            </Field>
          </DrawerSection>
        </div>
      </form>
    </Drawer>
  );
};

export default ServerModal;
